import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/libs/shadcn';

const badgeVariants = cva(
  'group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a]:hover:bg-primary/80',
        secondary:
          'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
        destructive:
          'bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20',
        outline:
          'border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
        ghost:
          'hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50',
        link: 'text-primary underline-offset-4 hover:underline',
        warning: 'bg-amber-400 text-white [a]:hover:bg-amber-400/80',
        success: 'bg-emerald-500 text-white [a]:hover:bg-emerald-500/80',
        info: 'bg-blue-500 text-white [a]:hover:bg-blue-500/80',
        neutral: 'bg-gray-400 text-white [a]:hover:bg-gray-400/80',
        // User Badge Color
        mentor: 'bg-indigo-500 text-white [a]:hover:bg-indigo-500/80',
        user: 'bg-blue-500 text-white [a]:hover:bg-blue-500/80',
        // User Status Badge Color
        'active-user': 'bg-primary-500 text-white border border-primary-600',
        'inactive-user': 'bg-gray-500 text-white  border border-gray-600',
        // User verification Badge Color
        'verified-user': 'bg-blue-500 text-white border border-blue-600',
        'unverified-user': 'bg-orange-500 text-white border border-orange-600',
        // Session Mentoring Badge Color
        'incoming-session': 'bg-blue-500 text-white border border-blue-600',
        'completed-session':
          'bg-primary-500 text-white border border-primary-600',
        // Product Price Badge Color
        'product-free': 'bg-blue-500 text-white border border-blue-600',
        // Product Badge Color
        'product-event':
          'bg-purple-50 text-purple-600 border border-purple-200',
        'product-tryout':
          'bg-orange-50 text-orange-600 border border-orange-200',
        'product-digital': 'bg-blue-50 text-blue-600 border border-blue-200',
        'product-bundle': 'bg-teal-50 text-teal-600 border border-teal-200',
        mentoring: 'bg-pink-50 text-pink-600 border border-pink-200',
        // Product Status Color
        'product-draft': 'bg-yellow-300 text-white border border-yellow-400',
        'product-published':
          'bg-primary-500 text-white border border-primary-600',
        'product-archived': 'bg-gray-400 text-white border border-gray-500',
        'product-private': 'bg-slate-600 text-white border border-slate-700',
        // Voucher Type Badge Color
        'voucher-percent':
          'bg-[#16BDCA] text-white [a]:hover:bg-[#16BDCA]/80 border border-[#0694A2]',
        'voucher-flat': 'bg-primary-500 text-white border border-primary-600',
        // Voucher Status Color
        'voucher-active': 'bg-primary-500 text-white border border-primary-600',
        'voucher-expired': 'bg-gray-400 text-white border border-gray-500',
        // Order Status Color
        'order-pending': 'bg-yellow-300 text-white border border-yellow-400',
        'order-failed': 'bg-red-600 text-white border border-red-700',
        'order-success': 'bg-primary-500 text-white border border-primary-600',
        // Transaction Type Badge Color
        'transaction-online':
          'bg-[#E8F5E9] text-[#2E7D32] border border-transparent',
        'transaction-fisik':
          'bg-[#E8F5E9] text-[#2E7D32] border border-transparent',
        'transaction-digital':
          'bg-[#E3F2FD] text-[#1565C0] border border-transparent',
        // Transaction Status Badge Color
        'transaction-success':
          'bg-[#E8F5E9] text-[#2E7D32] border border-transparent',
        'transaction-pending':
          'bg-[#FFF8E1] text-[#F57C00] border border-transparent',
        'transaction-failed':
          'bg-[#FFEBEE] text-[#D32F2F] border border-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];

function Badge({
  className,
  variant = 'default',
  render,
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  });
}

// Parse User Role variant
const USER_ROLE_VARIANT: Record<string, BadgeVariant> = {
  mentor: 'mentor',
  admin: 'info',
  user: 'user',
};

function parseForUserRoleBadge(userRole?: string): BadgeVariant {
  if (!userRole) return 'default';
  return USER_ROLE_VARIANT[userRole.toLowerCase()] ?? 'neutral';
}

// Parse User Status variant
const USER_STATUS_VARIANT: Record<string, BadgeVariant> = {
  active: 'active-user',
  inactive: 'inactive-user',
};

function parseForUserStatusBadge(userStatus?: string): BadgeVariant {
  if (!userStatus) return 'default';
  return USER_STATUS_VARIANT[userStatus.toLowerCase()] ?? 'neutral';
}

// Parse User Verified variant
const USER_VERIFIED_VARIANT: Record<string, BadgeVariant> = {
  verified: 'verified-user',
  unverified: 'unverified-user',
};

function parseForUserVerifiedBadge(isVerified: boolean): BadgeVariant {
  return isVerified
    ? USER_VERIFIED_VARIANT['verified']
    : USER_VERIFIED_VARIANT['unverified'];
}

// Parse User Verified variant
const SESSION_MENTORING_VARIANT: Record<string, BadgeVariant> = {
  incoming: 'incoming-session',
  completed: 'completed-session',
  no_show_pending: 'warning',
  awaiting_user_confirmation: 'warning',
  canceled: 'neutral',
  mentor_no_show: 'destructive',
};

function parseForSessionMentoringBadge(
  sessionMentoring?: string | null
): BadgeVariant {
  if (!sessionMentoring) return 'default';
  return SESSION_MENTORING_VARIANT[sessionMentoring.toLowerCase()] ?? 'neutral';
}

// Parse User Verified variant
const PRODUCT_VARIANT: Record<string, BadgeVariant> = {
  event: 'product-event',
  product_event: 'product-event',
  digital: 'product-digital',
  product_digital: 'product-digital',
  exam: 'product-tryout',
  product_exam: 'product-tryout',
  bundle: 'product-bundle',
  product_bundle: 'product-bundle',
  mentoring: 'mentoring',
};

function parseForProductBadge(product?: string | null): BadgeVariant {
  if (!product) return 'default';

  return PRODUCT_VARIANT[product.toLowerCase()] ?? 'neutral';
}

// Parse User Verified variant
const PRODUCT_STATUS_VARIANT: Record<string, BadgeVariant> = {
  draft: 'product-draft',
  published: 'product-published',
  archived: 'product-archived',
  private: 'product-private',
};

function parseForProductStatusBadge(
  productStatus?: string | null
): BadgeVariant {
  if (!productStatus) return 'default';

  return PRODUCT_STATUS_VARIANT[productStatus.toLowerCase()] ?? 'neutral';
}

// Parse User Verified variant
const VOUCHER_TYPE: Record<string, BadgeVariant> = {
  flat: 'voucher-flat',
  percentage: 'voucher-percent',
};

function parseForVoucherTypeBadge(voucherType?: string | null): BadgeVariant {
  if (!voucherType) return 'default';

  return VOUCHER_TYPE[voucherType.toLowerCase()] ?? 'neutral';
}

// Parse User Verified variant
const VOUCHER_STATUS: Record<string, BadgeVariant> = {
  active: 'voucher-active',
  expired: 'voucher-expired',
};

function parseForVoucherStatusBadge(isExpired?: boolean): BadgeVariant {
  return isExpired ? VOUCHER_STATUS['expired'] : VOUCHER_STATUS['active'];
}

// Parse User Verified variant
const ORDER_STATUS: Record<string, BadgeVariant> = {
  pending: 'order-pending',
  failed: 'order-failed',
  success: 'order-success',
};

function parseForOrderStatusBadge(orderStatus?: string | null): BadgeVariant {
  if (!orderStatus) return 'default';

  return ORDER_STATUS[orderStatus.toLowerCase()] ?? 'neutral';
}

// Parse Transaction Type variant
const TRANSACTION_TYPE_VARIANT: Record<string, BadgeVariant> = {
  online: 'transaction-online',
  fisik: 'transaction-fisik',
  digital: 'transaction-digital',
};

function parseForTransactionTypeBadge(type?: string | null): BadgeVariant {
  if (!type) return 'default';
  return TRANSACTION_TYPE_VARIANT[type.toLowerCase()] ?? 'neutral';
}

// Parse Transaction Status variant
const TRANSACTION_STATUS_VARIANT: Record<string, BadgeVariant> = {
  success: 'transaction-success',
  pending: 'transaction-pending',
  failed: 'transaction-failed',
};

function parseForTransactionStatusBadge(status?: string | null): BadgeVariant {
  if (!status) return 'default';
  return TRANSACTION_STATUS_VARIANT[status.toLowerCase()] ?? 'neutral';
}

export {
  Badge,
  badgeVariants,
  type BadgeVariant,
  parseForUserRoleBadge,
  parseForUserStatusBadge,
  parseForUserVerifiedBadge,
  parseForSessionMentoringBadge,
  parseForProductBadge,
  parseForProductStatusBadge,
  parseForVoucherStatusBadge,
  parseForVoucherTypeBadge,
  parseForOrderStatusBadge,
  parseForTransactionTypeBadge,
  parseForTransactionStatusBadge,
};
